import type { OnboardingDraft, OnboardingStep, ScreeningPromptItem } from './types';

/** Only http(s)/relative media URLs may be persisted — never browser blob: previews. */
export function isPersistedMediaUrl(url: string | null | undefined): boolean {
  const value = (url || '').trim();
  if (!value || value === 'N/A') return false;
  if (value.startsWith('blob:') || value.startsWith('draft://')) return false;
  return value.startsWith('https://') || value.startsWith('http://') || value.startsWith('/');
}

export function buildSaveDraftFormData(params: {
  draft: OnboardingDraft;
  linkedin: string;
  targetRole: string;
  step: OnboardingStep;
  promptItems: ScreeningPromptItem[];
  videoFiles: Record<number, File>;
  documentFiles?: Record<number, File>;
  videoUrls?: Record<number, string>;
  documentUrls?: Record<number, string>;
}): FormData {
  const formData = new FormData();
  const sanitizedItems = params.promptItems.map((item, index) => {
    const videoUrl = params.videoUrls?.[index] || item.fileUrl || '';
    const docUrl = params.documentUrls?.[index] || item.supportedDocumentUrl || '';
    return {
      ...item,
      fileUrl: isPersistedMediaUrl(videoUrl) ? videoUrl.trim() : '',
      supportedDocumentUrl: isPersistedMediaUrl(docUrl) ? docUrl.trim() : '',
    };
  });

  const mergedDraft: OnboardingDraft = {
    ...params.draft,
    targetRole: params.targetRole.trim() || params.draft.targetRole,
    screeningPromptItems: sanitizedItems,
    screeningPrompts: sanitizedItems.map((item) => item.content),
  };

  formData.append('draft', JSON.stringify(mergedDraft));
  formData.append('linkedin', params.linkedin.trim());
  formData.append('targetRole', params.targetRole.trim());
  formData.append('step', params.step);

  const promptsData = sanitizedItems.map((item, index) => ({
    content: item.content,
    description: item.description || '',
    ...(item.id ? { id: item.id } : {}),
    ...(item.fileUrl
      ? { fileUrl: item.fileUrl }
      : params.videoFiles[index]
        ? { fileId: `file-${index}` }
        : {}),
    ...(item.supportedDocumentUrl
      ? { supportedDocumentUrl: item.supportedDocumentUrl }
      : params.documentFiles?.[index]
        ? { supportDocumentFileId: `doc-${index}` }
        : {}),
  }));
  formData.append('prompts', JSON.stringify(promptsData));

  Object.entries(params.videoFiles).forEach(([key, file]) => {
    const index = Number(key);
    if (!Number.isFinite(index) || isPersistedMediaUrl(params.videoUrls?.[index])) {
      return;
    }
    if (file instanceof File && file.name) {
      formData.append(`file-${index}`, file, file.name);
    }
  });

  Object.entries(params.documentFiles || {}).forEach(([key, file]) => {
    const index = Number(key);
    if (!Number.isFinite(index) || isPersistedMediaUrl(params.documentUrls?.[index])) {
      return;
    }
    if (file instanceof File && file.name) {
      formData.append(`doc-${index}`, file, file.name);
    }
  });

  return formData;
}

export function promptItemsFromDraft(
  draft: OnboardingDraft | null,
  savedItems?: ScreeningPromptItem[] | null
): ScreeningPromptItem[] {
  if (savedItems?.length) {
    return savedItems.map((item) => ({
      id: item.id,
      content: item.content || '',
      description: item.description || '',
      fileUrl: isPersistedMediaUrl(item.fileUrl) ? String(item.fileUrl) : '',
      supportedDocumentUrl: isPersistedMediaUrl(item.supportedDocumentUrl)
        ? String(item.supportedDocumentUrl)
        : '',
    }));
  }
  const fromDraft = draft?.screeningPromptItems;
  if (fromDraft?.length) {
    return fromDraft.map((item) => ({
      id: item.id,
      content: item.content || '',
      description: item.description || '',
      fileUrl: isPersistedMediaUrl(item.fileUrl) ? String(item.fileUrl) : '',
      supportedDocumentUrl: isPersistedMediaUrl(item.supportedDocumentUrl)
        ? String(item.supportedDocumentUrl)
        : '',
    }));
  }
  return (draft?.screeningPrompts || []).map((content) => ({
    content: content || '',
    description: '',
    fileUrl: '',
    supportedDocumentUrl: '',
  }));
}
